import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, Button, Input } from '../components';
import api from '../services/api';
import toast from 'react-hot-toast';
import { ArrowLeft, Save, X, Plus, Download } from 'lucide-react';
import { PoortwachterDocumenten } from '../components/PoortwachterDocumenten';
import { AcademyAdviesKaart } from '../components/AcademyAdviesKaart';

/** Arbo-disciplines die Wet Verbetering Poortwachter-documenten opstellen. */
const POORTWACHTER_TYPES = new Set([
  'bedrijfsarts_arboarts',
  'praktijkondersteuner_bedrijfsarts',
  'arboverpleegkundige',
  'arbeidsdeskundige',
  'casemanager_regie_verzuim',
]);

const STATUS_OPTIONS = [
  { value: 'nieuwe_aanmelding', labelKey: 'status.nieuwe_aanmelding' },
  { value: 'intake_ingepland', labelKey: 'status.intake_ingepland' },
  { value: 'intake_uitgevoerd', labelKey: 'status.intake_uitgevoerd' },
  { value: 'eerste_gesprek_ingepland', labelKey: 'status.eerste_gesprek_ingepland' },
  { value: 'eerste_gesprek_uitgevoerd', labelKey: 'status.eerste_gesprek_uitgevoerd' },
  { value: 'tweede_gesprek_ingepland', labelKey: 'status.tweede_gesprek_ingepland' },
  { value: 'tweede_gesprek_uitgevoerd', labelKey: 'status.tweede_gesprek_uitgevoerd' },
  { value: 'derde_gesprek_ingepland', labelKey: 'status.derde_gesprek_ingepland' },
  { value: 'derde_gesprek_uitgevoerd', labelKey: 'status.derde_gesprek_uitgevoerd' },
  { value: 'vierde_gesprek_ingepland', labelKey: 'status.vierde_gesprek_ingepland' },
  { value: 'vierde_gesprek_uitgevoerd', labelKey: 'status.vierde_gesprek_uitgevoerd' },
  { value: 'vijfde_gesprek_ingepland', labelKey: 'status.vijfde_gesprek_ingepland' },
  { value: 'vijfde_gesprek_uitgevoerd', labelKey: 'status.vijfde_gesprek_uitgevoerd' },
  { value: 'lopend', labelKey: 'status.lopend' },
  { value: 'eindgesprek', labelKey: 'status.eindgesprek' },
  { value: 'afsluiten', labelKey: 'status.afsluiten' },
  { value: 'archived', labelKey: 'status.archived' },
  { value: 'on_hold', labelKey: 'status.on_hold' },
];

export const ProfessionalDossierDetailPage: React.FC = () => {
  const { t } = useTranslation('professional');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id, dossierId } = useParams<{ id?: string; dossierId?: string }>();
  const actualDossierId = dossierId || id;

  const [formData, setFormData] = useState<any>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Fetch dossier data
  const { data: dossier, isLoading, error } = useQuery({
    queryKey: ['professional-dossier', actualDossierId],
    queryFn: async () => {
      const res = await api.get(`/professional/dossiers/${actualDossierId}`);
      return res.data?.data;
    },
    enabled: !!actualDossierId,
  });

  useEffect(() => {
    if (dossier) {
      setFormData({
        status: dossier.status || '',
        client_name: dossier.client_name || '',
        client_address: dossier.client_address || '',
        client_phone: dossier.client_phone || '',
        client_email: dossier.client_email || '',
        client_function: dossier.client_function || '',
        client_department: dossier.client_department || '',
      });
      setHasChanges(false);
    }
  }, [dossier]);

  // Fetch related data
  const { data: notes } = useQuery({
    queryKey: ['dossier-notes', actualDossierId],
    queryFn: async () => {
      const res = await api.get('/professional/notes');
      const notes = res.data?.data || [];
      return notes.filter((note: any) => note.dossier_id === actualDossierId);
    },
    enabled: !!actualDossierId,
  });

  const { data: actionPoints } = useQuery({
    queryKey: ['dossier-action-points', actualDossierId],
    queryFn: async () => {
      const res = await api.get('/professional/action-points');
      const actionPoints = res.data?.data || [];
      return actionPoints.filter((ap: any) => ap.dossier_id === actualDossierId);
    },
    enabled: !!actualDossierId,
  });

  const { data: verslagen } = useQuery({
    queryKey: ['dossier-verslagen', actualDossierId],
    queryFn: async () => {
      const res = await api.get(`/professional/dossiers/${actualDossierId}/verslagen`);
      return res.data?.data || [];
    },
    enabled: !!actualDossierId,
  });

  const { data: hoursRegistrations } = useQuery({
    queryKey: ['dossier-hours', actualDossierId],
    queryFn: async () => {
      const res = await api.get('/professional/hours');
      const hours = res.data?.data || [];
      return hours.filter((h: any) => h.dossier_id === actualDossierId);
    },
    enabled: !!actualDossierId,
  });

  const { data: invoices } = useQuery({
    queryKey: ['dossier-invoices', actualDossierId],
    queryFn: async () => {
      const res = await api.get('/invoices/professional');
      const invoices = res.data?.data || [];
      return invoices.filter((inv: any) => inv.dossier_id === actualDossierId);
    },
    enabled: !!actualDossierId,
  });

  // Update dossier mutation
  const updateDossierMutation = useMutation({
    mutationFn: async (data: any) => {
      const res = await api.patch(`/professional/dossiers/${actualDossierId}`, data);
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('dossierDetail.changesSaved'));
      queryClient.invalidateQueries({ queryKey: ['professional-dossier', actualDossierId] });
      queryClient.invalidateQueries({ queryKey: ['professional-dossiers'] });
      setHasChanges(false);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('dossierDetail.changesSaveError'));
    },
  });

  // Close dossier mutation
  const closeDossierMutation = useMutation({
    mutationFn: async () => {
      const res = await api.patch(`/professional/dossiers/${actualDossierId}`, {
        status: 'afsluiten',
        end_date: new Date().toISOString()
      });
      return res.data;
    },
    onSuccess: () => {
      toast.success(t('dossierDetail.dossierClosedReview'));
      queryClient.invalidateQueries({ queryKey: ['professional-dossier', actualDossierId] });
      queryClient.invalidateQueries({ queryKey: ['professional-dossiers'] });
      navigate('/professional/cockpit');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || t('dossierDetail.dossierCloseError'));
    },
  });

  const handleInputChange = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateDossierMutation.mutate(formData);
  };

  const handleCloseDossier = () => {
    if (window.confirm(t('dossierDetail.confirmClose'))) {
      closeDossierMutation.mutate();
    }
  };

  // Group hours by month
  const hoursByMonth = React.useMemo(() => {
    if (!hoursRegistrations) return {};
    const grouped: Record<string, any[]> = {};
    hoursRegistrations.forEach((hour: any) => {
      const date = new Date(hour.datum);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!grouped[monthKey]) grouped[monthKey] = [];
      grouped[monthKey].push(hour);
    });
    return grouped;
  }, [hoursRegistrations]);

  if (!actualDossierId) return null;

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-10">
        <Card>
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="mt-2 text-gray-600">{t('dossierDetail.loading')}</p>
          </div>
        </Card>
      </div>
    );
  }

  if (!dossier) {
    const errorMessage = error && (error as any)?.response?.data?.message
      ? (error as any).response.data.message
      : t('dossierDetail.notFound');

    return (
      <div className="max-w-6xl mx-auto px-4 py-10">
        <Card>
          <div className="p-6 text-center">
            <p className="text-sm text-gray-700 mb-4">{errorMessage}</p>
            <Button variant="outline" onClick={() => navigate('/professional/cockpit')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('dossierDetail.backToOverview')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Button variant="outline" onClick={() => navigate('/professional/cockpit')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('dossierDetail.backToOverview')}
        </Button>
      </div>

      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('detail.dossier')}{dossier.client_name}</h1>
        <p className="text-sm text-gray-600 mt-1">{t('dossierDetail.dossierNumber')}: {dossier.id}</p>
      </div>

      <div className="space-y-6">
        {/* Gegevens Opdrachtgever */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dossierDetail.employerDetails')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.companyName')}</label>
                <div className="text-sm text-gray-900">{dossier.employer_company || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.contactPerson')}</label>
                <div className="text-sm text-gray-900">
                  {dossier.employer_contact_person || `${dossier.employer_first_name || ''} ${dossier.employer_last_name || ''}`.trim() || '-'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.phone')}</label>
                <div className="text-sm text-gray-900">{dossier.employer_phone || '-'}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.email')}</label>
                <div className="text-sm text-gray-900">{dossier.employer_email || '-'}</div>
              </div>
            </div>
          </div>
        </Card>

        {/* Gegevens Klant */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dossierDetail.clientDetails')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.nameRequired')}</label>
                <Input
                  value={formData.client_name || ''}
                  onChange={(e) => handleInputChange('client_name', e.target.value)}
                  placeholder={t('dossierDetail.clientNamePlaceholder')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.address')}</label>
                <Input
                  value={formData.client_address || ''}
                  onChange={(e) => handleInputChange('client_address', e.target.value)}
                  placeholder={t('dossierDetail.address')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.phone')}</label>
                <Input
                  value={formData.client_phone || ''}
                  onChange={(e) => handleInputChange('client_phone', e.target.value)}
                  placeholder={t('dossierDetail.phone')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.email')}</label>
                <Input
                  type="email"
                  value={formData.client_email || ''}
                  onChange={(e) => handleInputChange('client_email', e.target.value)}
                  placeholder={t('dossierDetail.email')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.functionOptional')}</label>
                <Input
                  value={formData.client_function || ''}
                  onChange={(e) => handleInputChange('client_function', e.target.value)}
                  placeholder={t('dossierDetail.function')}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('dossierDetail.departmentOptional')}</label>
                <Input
                  value={formData.client_department || ''}
                  onChange={(e) => handleInputChange('client_department', e.target.value)}
                  placeholder={t('dossierDetail.department')}
                />
              </div>
            </div>
          </div>
        </Card>

        {/* Status Traject */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('table.trajectStatus')}</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('table.status')}</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.status || ''}
                onChange={(e) => handleInputChange('status', e.target.value)}
              >
                {STATUS_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{t(opt.labelKey)}</option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        {/* Wet Verbetering Poortwachter — alleen voor arbo-disciplines */}
        {POORTWACHTER_TYPES.has(dossier.professional_type) && (
          <Card>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                {t('dossierDetail.poortwachter', 'Wet Verbetering Poortwachter')}
              </h2>
              <PoortwachterDocumenten dossierId={actualDossierId} />
            </div>
          </Card>
        )}

        {/* Belagom Expert-Aanbeveling: Academy advies-traject voor de medewerker */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {t('academyAdvies.titel', 'Academy advies-traject')}
            </h2>
            <AcademyAdviesKaart dossierId={actualDossierId} />
          </div>
        </Card>

        {/* Notities */}
        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('sidebar.notes')}</h2>
              <Link to={`/professional/notes?dossier_id=${actualDossierId}`}>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" /> {t('cockpit.newNote')}
                </Button>
              </Link>
            </div>
            {notes && notes.length > 0 ? (
              <div className="space-y-3">
                {notes.map((note: any) => (
                  <div key={note.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{note.titel}</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{note.notitie}</p>
                        <p className="text-xs text-gray-500 mt-2">
                          {new Date(note.aangemaakt_op).toLocaleDateString('nl-NL')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t('empty.noNotes')}</p>
            )}
          </div>
        </Card>

        {/* Actiepunten */}
        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('sidebar.actionPoints')}</h2>
              <Link to={`/professional/action-items?dossier_id=${actualDossierId}`}>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" /> {t('cockpit.newActionPoint')}
                </Button>
              </Link>
            </div>
            {actionPoints && actionPoints.length > 0 ? (
              <div className="space-y-3">
                {actionPoints.map((ap: any) => (
                  <div key={ap.id} className={`p-3 rounded-lg ${ap.voltooid ? 'bg-gray-50 opacity-60' : 'bg-yellow-50'}`}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{ap.titel_actiepunt}</h3>
                        <p className="text-sm text-gray-600 whitespace-pre-wrap">{ap.actie_omschrijving}</p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span>{t('detail.deadline')}{new Date(ap.deadline).toLocaleDateString('nl-NL')}</span>
                          {ap.voltooid && <span className="text-green-600">{t('cockpit.completed')}</span>}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t('empty.noActionPoints')}</p>
            )}
          </div>
        </Card>

        {/* Gespreksverslagen */}
        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('sidebar.conversationReports')}</h2>
              <Link to={`/professional/dossiers/${actualDossierId}/verslagen/nieuw`}>
                <Button size="sm">
                  <Plus className="w-4 h-4 mr-2" /> {t('cockpit.newReport')}
                </Button>
              </Link>
            </div>
            {verslagen && verslagen.length > 0 ? (
              <div className="space-y-3">
                {verslagen.map((verslag: any) => (
                  <div key={verslag.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">{verslag.title || verslag.titel || t('dossierDetail.conversationReport')}</h3>
                        <p className="text-xs text-gray-500">
                          {new Date(verslag.verslag_datum).toLocaleDateString('nl-NL')}
                        </p>
                      </div>
                      <Link to={`/professional/dossiers/${actualDossierId}/verslagen/${verslag.id}`}>
                        <Button variant="outline" size="sm">{t('cockpit.viewReport')}</Button>
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t('empty.noConversationReports')}</p>
            )}
          </div>
        </Card>

        {/* Urenregistratie */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('sidebar.hoursRegistration')}</h2>
            {hoursRegistrations && hoursRegistrations.length > 0 ? (
              <div className="space-y-4">
                {Object.entries(hoursByMonth).sort(([a], [b]) => b.localeCompare(a)).map(([monthKey, hours]) => {
                  const [year, month] = monthKey.split('-');
                  const monthName = new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
                  const totalHours = hours.reduce((sum: number, h: any) => sum + (h.tijd_minuten || 0) / 60, 0);
                  const totalAmount = hours.reduce((sum: number, h: any) => sum + parseFloat(h.total_amount || 0), 0);

                  return (
                    <div key={monthKey} className="border border-gray-200 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3">{monthName}</h3>
                      <div className="space-y-2 mb-3">
                        {hours.map((hour: any) => (
                          <div key={hour.id} className="flex justify-between text-sm">
                            <span>{new Date(hour.datum).toLocaleDateString('nl-NL')}</span>
                            <span>{((hour.tijd_minuten || 0) / 60).toFixed(2)} {t('dossierDetail.hoursUnit')}</span>
                            <span>&euro;{parseFloat(hour.total_amount || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between font-semibold text-gray-900 pt-2 border-t">
                        <span>{t('dossierDetail.total')}</span>
                        <span>{totalHours.toFixed(2)} {t('dossierDetail.hoursUnit')}</span>
                        <span>&euro;{totalAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t('empty.noHoursRegistered')}</p>
            )}
          </div>
        </Card>

        {/* Facturen */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('sidebar.invoices')}</h2>
            {invoices && invoices.length > 0 ? (
              <div className="space-y-3">
                {invoices.map((invoice: any) => (
                  <div key={invoice.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {t('dossierDetail.invoice')} {invoice.invoice_number || invoice.id}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {t('dossierDetail.period')}: {invoice.invoice_month}/{invoice.invoice_year}
                        </p>
                        <p className="text-sm text-gray-600">
                          {t('dossierDetail.amount')}: &euro;{parseFloat(invoice.total_amount || 0).toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 mt-2">
                          {t('table.status')}: {invoice.status === 'paid' ? t('cockpit.paid') : invoice.status === 'pending' ? t('cockpit.open') : invoice.status}
                        </p>
                      </div>
                      {invoice.id && (
                        <a href={`/api/invoices/professional/${invoice.id}/pdf`} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm">
                            <Download className="w-4 h-4 mr-2" /> {t('dossierDetail.downloadPdf')}
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">{t('empty.noInvoices')}</p>
            )}
          </div>
        </Card>

        {/* Action Buttons */}
        <Card>
          <div className="p-6">
            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={() => navigate('/professional/cockpit')}>
                {t('dossierDetail.backToOverview')}
              </Button>
              <div className="flex gap-3">
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges || updateDossierMutation.isPending}
                  loading={updateDossierMutation.isPending}
                >
                  <Save className="w-4 h-4 mr-2" />
                  {t('dossierDetail.saveChanges')}
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCloseDossier}
                  disabled={closeDossierMutation.isPending || dossier.status === 'afsluiten'}
                  loading={closeDossierMutation.isPending}
                >
                  <X className="w-4 h-4 mr-2" />
                  {t('dossierDetail.closeDossier')}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
